import { type api, useInfiniteApiQuery, useTeam } from '@vrplatform/data';
import { useJournalEntriesTableFilter } from './useJournalEntriesTableFilter';
import { formatStringToAmountFilter } from '@vrplatform/ui-common';
import type { Maybe } from '@vrplatform/utils';
import type { JournalEntriesModalWhere } from './useJournalEntriesModal';

const LIMIT = 50;

export const STORYBOOK_JOURNAL_ENTRIES_QUERY_KEY = 'journal-entries-table';

type TableFilter = ReturnType<typeof useJournalEntriesTableFilter>['filter'];

type ApiFilter = NonNullable<
  api['/reports/journal-entries']['get']['parameters']['query']
>;

export const getJournalEntriesApiQueryFilter = ({
  limit,
  modalFilter,
  modalWhere,
  page,
  tableFilter,
}: {
  page: number | undefined;
  limit: number | undefined;
  tableFilter: TableFilter;
  modalFilter: Maybe<Partial<TableFilter>>;
  modalWhere: Maybe<JournalEntriesModalWhere>;
}): ApiFilter => {
  const filter = modalWhere
    ? { ...modalFilter, ...modalWhere.filter }
    : tableFilter;
  const amountObj = formatStringToAmountFilter(filter.amount);

  const amount: string | undefined = amountObj
    ? amountObj.type === 'eq'
      ? amountObj.centTotal.toString()
      : amountObj.type === 'gt'
        ? `${amountObj.centTotal}...`
        : amountObj.type === 'lt'
          ? `...${amountObj.centTotal}`
          : amountObj.type === 'between'
            ? `${amountObj.gtCentTotal}...${amountObj.ltCentTotal}`
            : undefined
    : undefined;

  return {
    currency: filter.currency || undefined,
    classifications: Array.isArray(filter.classifications)
      ? filter.classifications.join(',')
      : filter.classifications || undefined,
    entityTypes: filter.type || undefined,
    ledger: filter.ledger ?? undefined,
    types: filter.subType?.join(',') || undefined,
    party: filter.party || undefined,
    accountIds:
      (Array.isArray(filter.accountIds)
        ? filter.accountIds.join(',')
        : filter.accountIds) || undefined,
    amount,
    search: filter.search || undefined,
    limit,
    page,
    date: filter.date || undefined,
    isDateRangeEndInclusive: filter.date?.includes('...'),
    listingIds: filter.listingIds?.includes('unmapped')
      ? 'unmapped'
      : (Array.isArray(filter.listingIds)
          ? filter.listingIds.join(',')
          : filter.listingIds) || undefined,
    status: filter.status === 'all' ? undefined : filter.status || undefined,
    published: filter.published ? filter.published === 'published' : undefined,
    transactionIds: modalWhere?.transactionIds?.join(',') || undefined,
    reservationIds:
      modalWhere?.reservationIds?.join(',') ||
      (Array.isArray(filter.reservationIds)
        ? filter.reservationIds.join(',')
        : filter.reservationIds || undefined),
    ownerStatementIds: modalWhere?.ownerStatementIds?.join(',') || undefined,
    contactIds: Array.isArray(filter.contactIds)
      ? filter.contactIds.join(',')
      : filter.contactIds || undefined,
    bookingChannels: Array.isArray(filter.bookingChannels)
      ? filter.bookingChannels.join(',')
      : filter.bookingChannels || undefined,
    includeDetails: true,
    ids: modalWhere?.journalEntryIds?.join(',') || undefined,
    openingBalanceType: filter.openingBalanceType || undefined,
    listingOwnershipPeriodIds: Array.isArray(filter.listingOwnershipPeriodIds)
      ? filter.listingOwnershipPeriodIds.join(',')
      : filter.listingOwnershipPeriodIds || undefined,
  };
};

export function useJournalEntriesQuery({
  skip,
  modalFilter,
  modalWhere,
}: {
  skip: boolean;
  modalFilter: Maybe<Partial<TableFilter>>;
  modalWhere: Maybe<JournalEntriesModalWhere>;
}) {
  'use memo';

  const { filter: tableFilter } = useJournalEntriesTableFilter();

  const [{ id: teamId }] = useTeam();

  return useInfiniteApiQuery(
    async (api, signal, args, pageParam) => {
      const result = await api.GET('/reports/journal-entries', {
        params: {
          query: getJournalEntriesApiQueryFilter({
            page: pageParam,
            limit: LIMIT,
            tableFilter: args.tableFilter,
            modalFilter: args.modalFilter,
            modalWhere: args.modalWhere,
          }),
        },
        signal,
      });

      if (result.error) throw result.error;

      const data = result.data;

      return {
        list: data.data || [],
        aggregate: data.pagination.total,
        pagination: data.pagination,
      };
    },
    {
      initialPageParam: 1,
      skip,
      storybookQueryKey: STORYBOOK_JOURNAL_ENTRIES_QUERY_KEY,
      queryGroup: 'reports',
      uniqueQueryKey: 'journal-entries-table',
      variables: {
        teamId,
        tableFilter,
        modalFilter,
        modalWhere,
      },
      getNextPageParam: (lastPage) => {
        return lastPage.pagination?.nextPage ?? null; // return null if no more pages
      },
      hideNotification: true,
    }
  );
}
